<template>
  <div class="parent-dashboard">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">家长中心</h1>
          <div class="child-selector">
            <el-select v-model="selectedChildId" @change="switchChild" placeholder="选择孩子">
              <el-option 
                v-for="child in children" 
                :key="child.id" 
                :label="child.name" 
                :value="child.id">
                <span>{{ child.name }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ child.grade }}</span>
              </el-option>
            </el-select>
          </div>
        </div>
        <div class="header-right">
          <div class="child-info" v-if="currentChild">
            <div class="child-avatar">
              <img :src="currentChild.avatar || defaultAvatar" :alt="currentChild.name" />
            </div>
            <div class="child-details">
              <div class="child-name">{{ currentChild.name }}</div>
              <div class="child-grade">{{ currentChild.grade }} · {{ currentChild.className }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 学习概览 -->
    <div class="study-overview">
      <div class="overview-card study-time-card">
        <div class="card-icon">
          <i class="fa fa-clock"></i>
        </div>
        <div class="card-content">
          <div class="card-number">{{ studyOverview.todayStudyTime }}分钟</div>
          <div class="card-label">今日学习时长</div>
          <div class="card-trend" :class="studyOverview.studyTimeTrend > 0 ? 'positive' : 'negative'">
            <i class="fa" :class="studyOverview.studyTimeTrend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
            {{ Math.abs(studyOverview.studyTimeTrend) }}%
          </div>
        </div>
      </div>
      
      <div class="overview-card error-count-card">
        <div class="card-icon">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <div class="card-content">
          <div class="card-number">{{ studyOverview.errorCount }}</div>
          <div class="card-label">本周新增错题</div>
          <div class="card-trend" :class="studyOverview.errorTrend < 0 ? 'positive' : 'negative'">
            <i class="fa" :class="studyOverview.errorTrend < 0 ? 'fa-arrow-down' : 'fa-arrow-up'"></i>
            {{ Math.abs(studyOverview.errorTrend) }}%
          </div>
        </div>
      </div>
      
      <div class="overview-card score-card">
        <div class="card-icon">
          <i class="fa fa-chart-line"></i>
        </div>
        <div class="card-content">
          <div class="card-number">{{ studyOverview.latestScore }}分</div>
          <div class="card-label">最近测试成绩</div>
          <div class="card-trend" :class="studyOverview.scoreTrend > 0 ? 'positive' : 'negative'">
            <i class="fa" :class="studyOverview.scoreTrend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
            {{ Math.abs(studyOverview.scoreTrend) }}分
          </div>
        </div>
      </div>
      
      <div class="overview-card streak-card">
        <div class="card-icon">
          <i class="fa fa-fire"></i>
        </div>
        <div class="card-content">
          <div class="card-number">{{ studyOverview.studyStreak }}天</div>
          <div class="card-label">连续学习天数</div>
          <div class="card-extra">
            <span class="streak-status">{{ getStreakStatus() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧面板 -->
      <div class="left-panel">
        <!-- 学习进度 -->
        <div class="study-progress">
          <div class="section-header">
            <h3>学习进度</h3>
            <el-button type="text" @click="viewDetailedProgress">查看详情</el-button>
          </div>
          
          <div v-if="currentPlan" class="progress-content">
            <div class="plan-info">
              <div class="plan-title">{{ currentPlan.title }}</div>
              <div class="plan-period">{{ formatDate(currentPlan.startDate) }} - {{ formatDate(currentPlan.endDate) }}</div>
            </div>
            
            <div class="progress-visual">
              <div class="progress-circle">
                <svg width="100" height="100">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e6e6e6"
                    stroke-width="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#3498db"
                    stroke-width="6"
                    stroke-linecap="round"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="circumference - (currentPlan.progress / 100) * circumference"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div class="progress-text">
                  <span class="progress-value">{{ currentPlan.progress }}%</span>
                </div>
              </div>
              
              <div class="progress-details">
                <div class="detail-item">
                  <span class="detail-label">当前成绩</span>
                  <span class="detail-value">{{ currentPlan.currentScore }}分</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">目标成绩</span>
                  <span class="detail-value">{{ currentPlan.targetScore }}分</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">剩余天数</span>
                  <span class="detail-value">{{ getRemainingDays() }}天</span>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="no-plan">
            <div class="no-plan-icon">
              <i class="fa fa-calendar-plus"></i>
            </div>
            <p>孩子还没有制定学习计划</p>
            <el-button type="primary" size="small" @click="suggestCreatePlan">建议制定计划</el-button>
          </div>
        </div>

        <!-- 学习报告 -->
        <div class="study-reports">
          <div class="section-header">
            <h3>学习报告</h3>
            <div class="report-controls">
              <el-radio-group v-model="reportTimeRange" size="small" @change="loadStudyReports">
                <el-radio-button label="week">本周</el-radio-button>
                <el-radio-button label="month">本月</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          
          <div class="report-chart">
            <div ref="studyChart" class="chart"></div>
          </div>
          
          <div class="report-summary">
            <div class="summary-item">
              <div class="summary-label">平均每日学习</div>
              <div class="summary-value">{{ reportSummary.avgDailyStudy }}分钟</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">完成作业数</div>
              <div class="summary-value">{{ reportSummary.completedAssignments }}个</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">错题掌握率</div>
              <div class="summary-value">{{ reportSummary.errorMasteryRate }}%</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧面板 -->
      <div class="right-panel">
        <!-- 今日学习情况 -->
        <div class="today-study">
          <div class="section-header">
            <h3>今日学习情况</h3>
            <span class="study-status" :class="todayStudy.status">{{ getStudyStatusText() }}</span>
          </div>
          
          <div class="study-timeline">
            <div 
              v-for="activity in todayStudy.activities" 
              :key="activity.id"
              class="timeline-item"
            >
              <div class="timeline-time">{{ formatTime(activity.time) }}</div>
              <div class="timeline-content">
                <div class="activity-icon" :class="activity.type">
                  <i class="fa" :class="getActivityIcon(activity.type)"></i>
                </div>
                <div class="activity-info">
                  <div class="activity-title">{{ activity.title }}</div>
                  <div class="activity-desc">{{ activity.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 最近错题 -->
        <div class="recent-errors">
          <div class="section-header">
            <h3>最近错题</h3>
          </div>
          
          <div v-if="recentErrors.length === 0" class="empty-tip">暂无最近错题</div>
          <div v-else class="error-list">
            <div 
              v-for="error in recentErrors" 
              :key="error.id"
              class="error-item"
              @click="viewErrorDetail(error)"
            >
              <div class="error-image">
                <img :src="error.imageUrl" :alt="error.subject" />
              </div>
              <div class="error-info">
                <div class="error-subject">{{ error.subject }}</div>
                <div class="error-knowledge">{{ error.knowledgePoint }}</div>
                <div class="error-time">{{ formatDate(error.createTime) }}</div>
              </div>
              <div class="error-status" :class="error.status">
                {{ error.status === 'mastered' ? '已掌握' : '未掌握' }}
              </div>
            </div>
          </div>
        </div>

        <!-- 学习提醒 -->
        <div class="study-reminders">
          <div class="section-header">
            <h3>学习提醒</h3>
            <el-button type="text" @click="manageReminders">管理提醒</el-button>
          </div>
          
          <div class="reminder-list">
            <div 
              v-for="reminder in studyReminders" 
              :key="reminder.id"
              class="reminder-item"
              :class="{ active: reminder.enabled }"
            >
              <div class="reminder-icon">
                <i class="fa" :class="getReminderIcon(reminder.type)"></i>
              </div>
              <div class="reminder-content">
                <div class="reminder-title">{{ reminder.title }}</div>
                <div class="reminder-time">{{ reminder.time }}</div>
              </div>
              <div class="reminder-toggle">
                <el-switch 
                  v-model="reminder.enabled" 
                  @change="toggleReminder(reminder)"
                ></el-switch>
              </div>
            </div>
          </div>
        </div>

        <!-- 快速操作 -->
        <div class="quick-actions">
          <div class="section-header">
            <h3>快速操作</h3>
          </div>
          
          <div class="action-buttons">
            <el-button type="primary" @click="sendEncouragement">
              <i class="fa fa-heart"></i> 发送鼓励
            </el-button>
            <el-button @click="viewFullReport">
              <i class="fa fa-chart-bar"></i> 完整报告
            </el-button>
            <el-button @click="contactTeacher">
              <i class="fa fa-comments"></i> 联系老师
            </el-button>
            <el-button :loading="printing" :disabled="printing" @click="printErrorBook">
              <i class="fa fa-print"></i> {{ printing ? '提交中...' : '打印错题' }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 鼓励消息对话框 -->
    <el-dialog
      title="发送鼓励消息"
      :visible.sync="encouragementDialogVisible"
      width="500px"
      center
    >
      <div class="encouragement-content">
        <div class="encouragement-templates">
          <h4>选择模板消息</h4>
          <div class="template-list">
            <div 
              v-for="template in encouragementTemplates" 
              :key="template.id"
              class="template-item"
              :class="{ selected: selectedTemplate === template.id }"
              @click="selectTemplate(template)"
            >
              <div class="template-icon">{{ template.emoji }}</div>
              <div class="template-text">{{ template.message }}</div>
            </div>
          </div>
        </div>
        
        <div class="custom-message">
          <h4>自定义消息</h4>
          <el-input 
            v-model="customEncouragement" 
            type="textarea" 
            :rows="3"
            placeholder="输入您想对孩子说的话..."
          ></el-input>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="encouragementDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEncouragement">发送</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';
import * as echarts from 'echarts';

export default {
  name: 'ParentDashboard',
  data() {
    return {
      // 孩子信息
      children: [],
      selectedChildId: null,
      currentChild: null,
      defaultAvatar: '/img/def_img.png',
      
      // 学习概览
      studyOverview: {
        todayStudyTime: 0,
        studyTimeTrend: 0,
        errorCount: 0,
        errorTrend: 0,
        latestScore: 0,
        scoreTrend: 0,
        studyStreak: 0
      },
      
      // 学习计划
      currentPlan: null,
      circumference: 2 * Math.PI * 40,
      
      // 学习报告
      reportTimeRange: 'week',
      reportSummary: {
        avgDailyStudy: 0,
        completedAssignments: 0,
        errorMasteryRate: 0
      },
      
      // 今日学习
      todayStudy: {
        status: 'active', // active, completed, inactive
        activities: []
      },
      
      // 最近错题
      recentErrors: [],
      
      // 学习提醒
      studyReminders: [],
      
      // 鼓励消息
      encouragementDialogVisible: false,
      selectedTemplate: null,
      customEncouragement: '',
      encouragementTemplates: [
        { id: 1, emoji: '👏', message: '今天学习很认真，继续加油！' },
        { id: 2, emoji: '🌟', message: '你的进步让我很骄傲！' },
        { id: 3, emoji: '💪', message: '坚持就是胜利，你做得很好！' },
        { id: 4, emoji: '🎉', message: '错题掌握得不错，再接再厉！' },
        { id: 5, emoji: '❤️', message: '爸爸妈妈相信你一定能达到目标！' }
      ],
      
      // 图表实例
      studyChart: null,
      printing: false
    }
  },
  created() {
    // recent-list 不依赖孩子选择，刷新页面时直接请求
    this.loadRecentErrors();
    this.loadChildren();
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
    });
  },
  beforeDestroy() {
    if (this.studyChart) {
      this.studyChart.dispose();
    }
  },
  methods: {
    async loadChildren() {
      try {
        const response = await axios.get('/parent/children');
        this.children = response.data.data || [];
        
        if (this.children.length > 0) {
          this.selectedChildId = this.children[0].id;
          this.switchChild();
        }
      } catch (error) {
        console.error('加载孩子信息失败:', error);
        this.$message.error('加载数据失败');
      }
    },
    
    async switchChild() {
      if (!this.selectedChildId) return;
      
      this.currentChild = this.children.find(child => child.id === this.selectedChildId);
      
      try {
        await Promise.all([
          this.loadStudyOverview(),
          this.loadCurrentPlan(),
          this.loadStudyReports(),
          this.loadTodayStudy(),
          this.loadStudyReminders()
        ]);
      } catch (error) {
        console.error('加载孩子数据失败:', error);
      }
    },
    
    async loadStudyOverview() {
      const response = await axios.get(`/parent/child/${this.selectedChildId}/overview`);
      this.studyOverview = response.data.data;
    },
    
    async loadCurrentPlan() {
      const response = await axios.get(`/parent/child/${this.selectedChildId}/current-plan`);
      this.currentPlan = response.data.data;
    },
    
    async loadStudyReports() {
      const response = await axios.get(`/parent/child/${this.selectedChildId}/study-reports`, {
        params: { timeRange: this.reportTimeRange }
      });
      const data = response.data.data;
      this.reportSummary = data.summary;
      this.updateStudyChart(data.chartData);
    },
    
    async loadTodayStudy() {
      const response = await axios.get(`/parent/child/${this.selectedChildId}/today-study`);
      this.todayStudy = response.data.data;
    },
    
    async loadRecentErrors() {
      try {
        const response = await axios.get('/requestErrorRecord/recent-list');
        const res = response.data;
        if (res.code !== 200) {
          this.recentErrors = [];
          this.$message.error(res.message || '加载最近错题失败');
          return;
        }
        this.recentErrors = (res.data || []).filter(item => item && item.id).map(item => ({
          ...item,
          status: item.mastered ? 'mastered' : 'unmastered',
          subject: item.subject || '未知',
          knowledgePoint: item.knowledgePoint || '未知',
          imageUrl: item.paperCutImg || item.dealImgUrl || '',
          createTime: item.createTime || new Date()
        }));
      } catch (error) {
        console.error('加载最近错题失败:', error);
        this.recentErrors = [];
        this.$message.error('加载最近错题失败');
      }
    },
    
    async loadStudyReminders() {
      const response = await axios.get(`/parent/child/${this.selectedChildId}/reminders`);
      this.studyReminders = response.data.data || [];
    },
    
    initCharts() {
      if (this.$refs.studyChart) {
        this.studyChart = echarts.init(this.$refs.studyChart);
      }
    },
    
    updateStudyChart(chartData) {
      if (!this.studyChart || !chartData) return;
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        xAxis: {
          type: 'category',
          data: chartData.dates
        },
        yAxis: {
          type: 'value',
          name: '学习时长(分钟)'
        },
        series: [{
          data: chartData.studyTimes,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#3498db'
          },
          areaStyle: {
            color: 'rgba(52, 152, 219, 0.1)'
          }
        }]
      };
      this.studyChart.setOption(option);
    },
    
    getRemainingDays() {
      if (!this.currentPlan) return 0;
      const endDate = moment(this.currentPlan.endDate);
      const now = moment();
      return Math.max(0, endDate.diff(now, 'days'));
    },
    
    getStreakStatus() {
      const streak = this.studyOverview.studyStreak;
      if (streak >= 30) return '学霸模式';
      if (streak >= 14) return '坚持不懈';
      if (streak >= 7) return '习惯养成';
      if (streak >= 3) return '初见成效';
      return '刚刚起步';
    },
    
    getStudyStatusText() {
      const statusMap = {
        active: '学习中',
        completed: '今日完成',
        inactive: '未开始'
      };
      return statusMap[this.todayStudy.status] || '未知';
    },
    
    getActivityIcon(type) {
      const iconMap = {
        login: 'fa-sign-in-alt',
        study: 'fa-book-open',
        practice: 'fa-pencil-alt',
        error: 'fa-exclamation-triangle',
        complete: 'fa-check-circle'
      };
      return iconMap[type] || 'fa-circle';
    },
    
    getReminderIcon(type) {
      const iconMap = {
        study: 'fa-book',
        practice: 'fa-pencil-alt',
        review: 'fa-redo',
        rest: 'fa-bed'
      };
      return iconMap[type] || 'fa-bell';
    },
    
    async toggleReminder(reminder) {
      try {
        await axios.put(`/parent/reminders/${reminder.id}`, {
          enabled: reminder.enabled
        });
        this.$message.success(reminder.enabled ? '提醒已开启' : '提醒已关闭');
      } catch (error) {
        console.error('更新提醒失败:', error);
        reminder.enabled = !reminder.enabled; // 回滚状态
        this.$message.error('操作失败');
      }
    },
    
    sendEncouragement() {
      this.encouragementDialogVisible = true;
      this.selectedTemplate = null;
      this.customEncouragement = '';
    },
    
    selectTemplate(template) {
      this.selectedTemplate = template.id;
      this.customEncouragement = template.message;
    },
    
    async submitEncouragement() {
      if (!this.customEncouragement.trim()) {
        this.$message.error('请输入鼓励消息');
        return;
      }
      
      try {
        await axios.post(`/parent/child/${this.selectedChildId}/encouragement`, {
          message: this.customEncouragement
        });
        
        this.$message.success('鼓励消息已发送');
        this.encouragementDialogVisible = false;
      } catch (error) {
        console.error('发送鼓励消息失败:', error);
        this.$message.error('发送失败');
      }
    },
    
    viewDetailedProgress() {
      this.$message.info('详细进度功能开发中');
    },
    
    suggestCreatePlan() {
      this.$confirm('建议孩子制定学习计划，是否发送提醒？', '提示', {
        confirmButtonText: '发送提醒',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        // 发送制定计划提醒
        this.$message.success('提醒已发送');
      });
    },
    
    viewErrorDetail() {
      this.$message.info('错题详情功能开发中');
    },
    
    manageReminders() {
      this.$message.info('提醒管理功能开发中');
    },
    
    viewFullReport() {
      this.$message.info('完整报告功能开发中');
    },
    
    contactTeacher() {
      this.$message.info('联系老师功能开发中');
    },
    
    async printErrorBook() {
      if (this.printing) {
        return;
      }
      if (!this.selectedChildId) {
        this.$message.warning('请先选择孩子');
        return;
      }
      this.printing = true;
      try {
        await axios.post(`/parent/child/${this.selectedChildId}/print-errors`);
        this.$message.success('错题本打印任务已提交');
      } catch (error) {
        console.error('打印错题本失败:', error);
        this.$message.error('打印失败');
      } finally {
        this.printing = false;
      }
    },
    
    formatDate(date) {
      return moment(date).format('MM-DD');
    },
    
    formatTime(time) {
      return moment(time).format('HH:mm');
    }
  }
}
</script>

<style lang="less" scoped>
.parent-dashboard {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

// 页面头部
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.child-selector {
  .el-select {
    width: 200px;
  }
}

.header-right {
  .child-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.child-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.child-details {
  .child-name {
    font-size: 16px;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 4px;
  }
  
  .child-grade {
    font-size: 14px;
    color: #7f8c8d;
  }
}

// 学习概览
.study-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.overview-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  i {
    font-size: 24px;
    color: white;
  }
}

.study-time-card .card-icon {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.error-count-card .card-icon {
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
}

.score-card .card-icon {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
}

.streak-card .card-icon {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.card-content {
  flex: 1;
}

.card-number {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.card-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 8px;
}

.card-trend {
  font-size: 12px;
  font-weight: 500;
  
  &.positive {
    color: #27ae60;
  }
  
  &.negative {
    color: #e74c3c;
  }
  
  i {
    margin-right: 4px;
  }
}

.card-extra {
  .streak-status {
    font-size: 12px;
    color: #3498db;
    font-weight: 500;
  }
}

// 主要内容区域
.main-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.left-panel, .right-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// 通用区块样式
.study-progress, .study-reports, .today-study, .recent-errors, .study-reminders, .quick-actions {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.view-all {
  color: #3498db;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    color: #2980b9;
  }
}

// 学习进度
.progress-content {
  .plan-info {
    margin-bottom: 20px;
    
    .plan-title {
      font-weight: 500;
      color: #2c3e50;
      margin-bottom: 4px;
    }
    
    .plan-period {
      font-size: 14px;
      color: #7f8c8d;
    }
  }
}

.progress-visual {
  display: flex;
  align-items: center;
  gap: 24px;
}

.progress-circle {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-value {
  font-size: 20px;
  font-weight: 700;
  color: #3498db;
}

.progress-details {
  flex: 1;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  
  .detail-label {
    color: #7f8c8d;
    font-size: 14px;
  }
  
  .detail-value {
    color: #2c3e50;
    font-weight: 500;
  }
}

.no-plan {
  text-align: center;
  padding: 40px 20px;
  
  .no-plan-icon {
    font-size: 48px;
    color: #bdc3c7;
    margin-bottom: 16px;
  }
  
  p {
    color: #7f8c8d;
    margin-bottom: 16px;
  }
}

// 学习报告
.report-chart {
  height: 200px;
  margin-bottom: 20px;
}

.chart {
  width: 100%;
  height: 100%;
}

.report-summary {
  display: flex;
  justify-content: space-around;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.summary-item {
  text-align: center;
  
  .summary-label {
    font-size: 12px;
    color: #7f8c8d;
    margin-bottom: 4px;
  }
  
  .summary-value {
    font-size: 16px;
    font-weight: 600;
    color: #3498db;
  }
}

// 今日学习情况
.study-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.active {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.completed {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  &.inactive {
    background-color: #f5f5f5;
    color: #757575;
  }
}

.study-timeline {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.timeline-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.timeline-time {
  font-size: 12px;
  color: #7f8c8d;
  min-width: 50px;
}

.timeline-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    font-size: 14px;
    color: white;
  }
  
  &.login {
    background-color: #3498db;
  }
  
  &.study {
    background-color: #27ae60;
  }
  
  &.practice {
    background-color: #f39c12;
  }
  
  &.error {
    background-color: #e74c3c;
  }
  
  &.complete {
    background-color: #9b59b6;
  }
}

.activity-info {
  .activity-title {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 2px;
  }
  
  .activity-desc {
    font-size: 12px;
    color: #7f8c8d;
  }
}

// 最近错题
.empty-tip {
  padding: 24px 12px;
  text-align: center;
  color: #95a5a6;
  font-size: 13px;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
}

.error-image {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 12px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.error-info {
  flex: 1;
}

.error-subject {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.error-knowledge {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.error-time {
  font-size: 11px;
  color: #95a5a6;
}

.error-status {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  
  &.mastered {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.unmastered {
    background-color: #f8d7da;
    color: #721c24;
  }
}

// 学习提醒
.reminder-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reminder-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &.active {
    background-color: #e3f2fd;
  }
}

.reminder-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #3498db;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  
  i {
    font-size: 14px;
    color: white;
  }
}

.reminder-content {
  flex: 1;
  
  .reminder-title {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 2px;
  }
  
  .reminder-time {
    font-size: 12px;
    color: #7f8c8d;
  }
}

// 快速操作
.action-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  .el-button {
    height: 40px;
    
    i {
      margin-right: 6px;
    }
  }
}

// 鼓励消息对话框
.encouragement-content {
  .encouragement-templates {
    margin-bottom: 24px;
    
    h4 {
      margin-bottom: 12px;
      color: #2c3e50;
    }
  }
  
  .template-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .template-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background-color: #f8f9fa;
    }
    
    &.selected {
      border-color: #3498db;
      background-color: #e3f2fd;
    }
  }
  
  .template-icon {
    font-size: 20px;
    margin-right: 12px;
  }
  
  .template-text {
    flex: 1;
    color: #2c3e50;
  }
  
  .custom-message {
    h4 {
      margin-bottom: 12px;
      color: #2c3e50;
    }
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .parent-dashboard {
    padding: 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
  }
  
  .header-left {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .study-overview {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .overview-card {
    padding: 16px;
  }
  
  .card-icon {
    width: 48px;
    height: 48px;
    margin-right: 12px;
    
    i {
      font-size: 20px;
    }
  }
  
  .card-number {
    font-size: 24px;
  }
  
  .progress-visual {
    flex-direction: column;
    gap: 16px;
  }
  
  .action-buttons {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .study-overview {
    grid-template-columns: 1fr;
  }
  
  .child-selector .el-select {
    width: 100%;
  }
}
</style>
